import { splitCsvLine } from "../../shared/functions/splitCsvLine";
import {
  normaliseSplitCostingDate,
  validateSplitCostingEmployeeCode,
  validateSplitCostingPath,
  validateSplitCostingPercentage,
} from "./splitCostingRowValidation";

// ─── Public types ────────────────────────────────────────────────────────────

/** One path level within a split costing row. */
export interface SplitCostingPathLevel {
  level: number;
  code: string;
}

/**
 * One successfully parsed data row. `apportionedPercentage` is kept as the raw
 * string from the file — numeric parsing (non-finite values) belongs to the API
 * (AC-ERR-04), but range validation (< 1 or > 100) is enforced here at parse
 * time (portal blocking check). `endDate` is normalised to `yyyy-MM-dd` at
 * parse time (same as `startDate`) so format errors surface next to the offending
 * row rather than as server errors at submit time.
 */
export interface SplitCostingRow {
  /** 1-based row number in the source file (header = 1, first data row = 2). */
  fileRow: number;
  employeeCode: string;
  /** Raw string from the file — e.g. `"60"`, not `0.6`. */
  apportionedPercentage: string;
  /** Normalised to `yyyy-MM-dd` at parse time; blank stays `null`. */
  startDate: string | null;
  /** Normalised to `yyyy-MM-dd` at parse time; blank stays `null`. */
  endDate: string | null;
  /** Path levels in order, stopping at the first blank (no trailing empties). */
  path: SplitCostingPathLevel[];
}

/** A structural or per-row parse error. */
export interface SplitCostingParseError {
  /** 1-based row number; `null` for schema-level errors that apply to the whole file. */
  fileRow: number | null;
  message: string;
}

/** Outcome of `parseSplitCostingCsv`. Discriminated on `ok`. */
export type ParseOutcome =
    | { ok: true; rows: SplitCostingRow[] }
  | { ok: false; errors: SplitCostingParseError[] };

/**
 * Maximum number of data rows accepted by the API (`MAX_ROWS_PER_INVOCATION`
 * in `split_costing_import_models.py`). Files exceeding this limit are rejected
 * here at parse time so the consultant sees a clear message before reaching the
 * validate/submit step — rather than a 422 from the server.
 */
export const MAX_ROWS = 500;

// ─── Column definitions ──────────────────────────────────────────────────────

/**
 * Normalise a header token for matching: lowercase, strip spaces/underscores/hyphens.
 * Mirrors the approach in `useWorkPatternImportCsvParser`.
 */
function normaliseHeader(h: string): string {
  return h.toLowerCase().replace(/[\s_-]/g, "");
}

// Canonical header strings and their normalised keys.
const COL_EMPLOYEE_CODE = "employeecode";
const COL_APPORTIONED_PERCENTAGE = "apportionedpercentage";
const COL_DIVISION = "division";
const COL_COST_CENTRE = "costcentre";
const COL_DEPARTMENT = "department";
const COL_SUB_DEPARTMENT = "subdepartment";
const COL_LEVEL_6 = "level6";
const COL_LEVEL_7 = "level7";
const COL_START_DATE = "startdate";
const COL_END_DATE = "enddate";

/** Normalised keys for the path-level columns in order (D3 defines level 1→6). */
const PATH_COLUMN_KEYS = [
  COL_DIVISION,
  COL_COST_CENTRE,
  COL_DEPARTMENT,
  COL_SUB_DEPARTMENT,
  COL_LEVEL_6,
  COL_LEVEL_7,
] as const;

/** Normalised key that must NOT appear in a valid portal upload CSV (R-APP-11). */
const FORBIDDEN_COL_EMPLOYEE_NAME = "employeename";

const REQUIRED_COLUMNS = [
  COL_EMPLOYEE_CODE,
  COL_APPORTIONED_PERCENTAGE,
  COL_DIVISION,
  COL_COST_CENTRE,
  COL_DEPARTMENT,
] as const;

// ─── Header scanning ──────────────────────────────────────────────────────────

/**
 * Scan `lines` from the top to find the header row — the first line that
 * contains `Employee Code` (case-/space-/underscore-insensitive). Returns the
 * 0-based line index and the normalised header array, or `null` if not found.
 * (D2: scan rather than assume a fixed offset.)
 */
function findHeaderRow(
  lines: string[]
): { lineIdx: number; normHeaders: string[]; rawHeaders: string[] } | null {
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === "") continue;
    const fields = splitCsvLine(line).map((h) => h.trim());
    const normed = fields.map(normaliseHeader);
    if (normed.includes(COL_EMPLOYEE_CODE)) {
      return { lineIdx: i, normHeaders: normed, rawHeaders: fields };
    }
  }
  return null;
}

// ─── Schema validation ────────────────────────────────────────────────────────

/**
 * Check that the header row passes schema requirements:
 * - `Employee Name` must not be present (AC-ERR-08, R-APP-11)
 * - All required columns must be present
 *
 * Returns an array of schema-level errors (empty = valid).
 */
function validateSchema(normHeaders: string[]): SplitCostingParseError[] {
  const errors: SplitCostingParseError[] = [];

  // R-APP-11: Employee Name must not be present — check before missing-column
  // errors so schema violations surface first.
  if (normHeaders.includes(FORBIDDEN_COL_EMPLOYEE_NAME)) {
    errors.push({
      fileRow: null,
      message:
        "Employee Name column must not be present in the portal upload CSV (R-APP-11). " +
        "Remove that column and re-export the Split Costing tab as CSV.",
    });
    // Return immediately — no point checking further.
    return errors;
  }

  for (const col of REQUIRED_COLUMNS) {
    if (!normHeaders.includes(col)) {
      const canonical = col
        .replace("employeecode", "Employee Code")
        .replace("apportionedpercentage", "Apportioned Percentage")
        .replace("division", "Division")
        .replace("costcentre", "Cost Centre")
        .replace("department", "Department");
      errors.push({
        fileRow: null,
        message: `Missing required column: ${canonical}`,
      });
    }
  }

  return errors;
}

// ─── Row parsing ──────────────────────────────────────────────────────────────

/**
 * Parse one data row into a `SplitCostingRow`.
 * Returns `{ row }` on success or `{ error }` on any parse failure.
 */
function parseRow(
  values: string[],
  normHeaders: string[],
  fileRow: number,
  options: ParseSplitCostingCsvOptions
): { row: SplitCostingRow } | { error: string } {
  const get = (normKey: string): string => {
    const idx = normHeaders.indexOf(normKey);
    return idx >= 0 ? (values[idx] ?? "").trim() : "";
  };

  const employeeCodeResult = validateSplitCostingEmployeeCode(
    get(COL_EMPLOYEE_CODE)
  );
  if (employeeCodeResult.error) return { error: employeeCodeResult.error };

  const percentageResult = validateSplitCostingPercentage(
    get(COL_APPORTIONED_PERCENTAGE),
    {
      allowNonNumeric: true,
      allowOutOfRange: options.retainOutOfRangePercentageRows,
    }
  );
  if (percentageResult.error) return { error: percentageResult.error };

  const pathResult = validateSplitCostingPath(
    PATH_COLUMN_KEYS.map((key) => get(key))
  );
  if (pathResult.error) return { error: pathResult.error };

  // Start date normalisation (shared onboarding formats → yyyy-MM-dd; blank → null).
  const startDateRaw = get(COL_START_DATE);
  const startDateResult = normaliseSplitCostingDate(
    startDateRaw,
    "Start date"
  );
  if (startDateResult.error) return { error: startDateResult.error };

  // End date: same normalisation as start date (#1064).
  const endDateRaw = get(COL_END_DATE);
  const endDateResult = normaliseSplitCostingDate(endDateRaw, "End date");
  if (endDateResult.error) return { error: endDateResult.error };

  return {
    row: {
      fileRow,
      employeeCode: employeeCodeResult.value!,
      apportionedPercentage: percentageResult.value!,
      startDate: startDateResult.value!,
      endDate: endDateResult.value!,
      path: pathResult.value!,
    },
  };
}

// ─── Public entry point ────────────────────────────────────────────────────────

/**
 * Parse raw CSV text into typed `SplitCostingRow[]`.
 *
 * - Header row is located by scanning for `Employee Code` (D2), not by fixed offset.
 * - `Employee Name` column blocks with a schema error before any row is processed
 *   (AC-ERR-08, R-APP-11).
 * - `Start date` / `End date` are normalised to `yyyy-MM-dd` at parse time via
 *   shared `parseOnboardingDate` (DD/MM/YYYY, DD-MM-YYYY, short years, ISO, YYYY/MM/DD;
 *   blank → null). Invalid format blocks the row here rather than at submit time.
 * - `Apportioned Percentage` is left as the raw string — non-finite values are
 *   caught by the API (AC-ERR-04), but range validation (< 1 or > 100) is a
 *   portal blocking error applied here at parse time.
 * - Parse errors carry the 1-based file row number so the UI can point at the line.
 *
 * Pure function — no React, no side effects.
 */
export interface ParseSplitCostingCsvOptions {
  /**
   * Clustered-report uploads retain finite values outside 1–100 so the
   * validation report can attach an editable issue to the original row.
   * Default false preserves the legacy wizard's parse-time block.
   */
  retainOutOfRangePercentageRows?: boolean;
}

export function parseSplitCostingCsv(
  rawText: string,
  options: ParseSplitCostingCsvOptions = {}
): ParseOutcome {
  // Strip leading UTF-8 BOM so Excel CSV exports match header tokens correctly.
  const text = rawText.startsWith("\uFEFF") ? rawText.slice(1) : rawText;

  const lines = text
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n");

  if (lines.every((l) => l.trim() === "")) {
    return {
      ok: false,
      errors: [{ fileRow: null, message: "The file is empty." }],
    };
  }

  const headerResult = findHeaderRow(lines);
  if (!headerResult) {
    return {
      ok: false,
      errors: [
        {
          fileRow: null,
          message:
            "Could not find a header row containing 'Employee Code'. " +
            "Check that you have exported the Split Costing tab as CSV.",
        },
      ],
    };
  }

  const { lineIdx, normHeaders } = headerResult;
  const schemaErrors = validateSchema(normHeaders);
  if (schemaErrors.length > 0) {
    return { ok: false, errors: schemaErrors };
  }

  const rowErrors: SplitCostingParseError[] = [];
  const rows: SplitCostingRow[] = [];

  for (let i = lineIdx + 1; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === "") continue;

    const values = splitCsvLine(line).map((v) => v.trim());
    if (values.every((v) => v === "")) continue;

    // fileRow is 1-based; lineIdx is 0-based, so header is at lineIdx+1 in file terms.
    const fileRow = i + 1;
    const result = parseRow(values, normHeaders, fileRow, options);

    if ("error" in result) {
      rowErrors.push({ fileRow, message: result.error });
    } else {
      rows.push(result.row);
    }
  }

  if (rowErrors.length > 0) {
    return { ok: false, errors: rowErrors };
  }

  if (rows.length > MAX_ROWS) {
    return {
      ok: false,
      errors: [
        {
          fileRow: null,
          message: `The file contains ${rows.length} data rows, which exceeds the ${MAX_ROWS}-row limit. Split the file and import each part separately.`,
        },
      ],
    };
  }

  if (rows.length === 0) {
    return {
      ok: false,
      errors: [
        {
          fileRow: null,
          message:
            "The file contains no data rows. Add at least one split costing row below the header.",
        },
      ],
    };
  }

  return { ok: true, rows };
}
